import { RoleId } from '@efexteam/permission-manager';
import { UserPreferencesResponse } from '@main-hooks/useUserPreferences/useUserPreferences.types';
import { TOKEN_TYPES } from '@constants/auth.constants';
import { AUTH_ENDPOINTS } from '../endpoints';
import { ICreateMember, MembersResponse } from '../../pages/profile/types';
import client from '../axios';
import { cognitoClient } from '../config';
import {
  AuthCredentials,
  CountriesList,
  FederalEntitiesList,
  IBanks,
  IGetWhiteLabel,
  IRegisterHubspotUser,
  IRegisterUser,
  IRegisterUserResponse,
  IWhiteLabel,
  RefreshToken,
  SetUpTwoFactorResponse,
  SetUpTwoFactorStep2Response,
  UserProfileRequest,
  UserProfileResponse,
  CognitoLoginResponse,
  CognitoRefreshTokenRequest,
  CognitoRefreshTokenResponse,
  CognitoConfirmPasswordRequest,
  UpdatePasswordRequest,
} from './types';
import { aesEncryptCBC } from '../../utils/crypt';

const userValidate = async () => client.post(AUTH_ENDPOINTS.USER_VALIDATE);

const login = async (
  data: AuthCredentials
): Promise<{ refresh: string; token: string; [key: string]: string }> =>
  client.post(AUTH_ENDPOINTS.LEGACY_LOGIN, data);

const cognitoLogin = async (
  data: AuthCredentials
): Promise<CognitoLoginResponse> => {
  try {
    const response = (await cognitoClient.post(
      AUTH_ENDPOINTS.LOGIN,
      data
    )) as CognitoLoginResponse;

    return response;
  } catch (error: any) {
    console.error('Auth request failed:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
    });
    throw error;
  }
};

const cognitoTokenRefresh = async (
  data: CognitoRefreshTokenRequest
): Promise<CognitoRefreshTokenResponse> => {
  try {
    const response = (await cognitoClient.post(
      AUTH_ENDPOINTS.REFRESH,
      data
    )) as CognitoRefreshTokenResponse;

    return response;
  } catch (error: any) {
    console.error('Auth token refresh failed:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
    });
    throw error;
  }
};

const tokenRefresh = async (data: RefreshToken): Promise<any> =>
  client.post(AUTH_ENDPOINTS.LEGACY_REFRESH, data);

const tokenBlacklist = async (data: RefreshToken) =>
  client.post(AUTH_ENDPOINTS.TOKEN_BLACKLIST, data);

const cognitoForgotPassword = async (email: string): Promise<any> => {
  try {
    const response = await cognitoClient.get(
      `${AUTH_ENDPOINTS.FORGOT_PASSWORD}?email=${encodeURIComponent(email)}`
    );
    return response;
  } catch (error: any) {
    console.error('Auth forgot password request failed:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
    });
    throw error;
  }
};

const cognitoConfirmPassword = async (
  data: CognitoConfirmPasswordRequest
): Promise<any> => {
  try {
    const response = await cognitoClient.post(AUTH_ENDPOINTS.CONFIRM_PASSWORD, {
      email: data.email,
      code: data.code,
      newPassword: data.newPassword,
    });
    return response;
  } catch (error: any) {
    console.error('Auth confirm password request failed:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
    });
    throw error;
  }
};

const cognitoLogout = async (accessToken: string): Promise<any> => {
  try {
    const response = await cognitoClient.post(AUTH_ENDPOINTS.LOGOUT, {
      accessToken,
    });
    return response;
  } catch (error: any) {
    console.error('Auth logout request failed:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
    });
    throw error;
  }
};

const getUserProfile = async (): Promise<UserProfileResponse> =>
  client.get('/user-profile/');

const getUserPreferences = async (
  userId: string
): Promise<UserPreferencesResponse> =>
  client.get(`/users/${userId}/preferences/`);

const updateUserPreference = async (
  userId: string,
  preference: string,
  value: string | boolean
): Promise<UserPreferencesResponse> =>
  client.put(`/users/${userId}/preferences/`, { key: preference, value });

const createUserPreference = async (
  userId: string,
  preference: string,
  value: string | boolean
): Promise<UserPreferencesResponse> =>
  client.post(`/users/${userId}/preferences/`, { key: preference, value });

const getTeam = async (enterpriseID: string): Promise<MembersResponse[]> =>
  client.get(`/entity/${enterpriseID}/users`);

const createTeamMember = async (
  enterpriseID: string,
  data: ICreateMember
): Promise<MembersResponse[]> =>
  client.post(`/entity/${enterpriseID}/users`, data);

const updateTeamMember = async (
  enterpriseID: string,
  data: {
    naturalPersonId: number;
    roleId: RoleId;
    status?: boolean;
  }
): Promise<MembersResponse[]> =>
  client.put(`/entity/${enterpriseID}/users`, data);

const deleteTeamMember = async (
  enterpriseID: string,
  naturalPersonId: number
): Promise<MembersResponse[]> =>
  client.delete(`/entity/${enterpriseID}/users?np=${naturalPersonId}`);

const updateProfileInformation = (data?: UserProfileRequest) =>
  client.put('/user-profile/', data);

const validateReferrer = (code?: string) =>
  client.get(`/validate-referrer/?code=${code}`);

const getBanks = (): Promise<IBanks[]> => client.get(`/banks/`);

const registerUser = async (
  data: IRegisterUser
): Promise<IRegisterUserResponse> =>
  client.post('/register/', data, {
    isAuthRedirect: false,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

const registerHubspotUser = async (data: IRegisterHubspotUser): Promise<any> =>
  client.post('/onboarding-user/', data, {
    isAuthRedirect: false,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

const validateRegisterEmail = (email: string): Promise<any> =>
  client.get(`/verification/person/?email=${email}`);

const validateInvitationUser = async ({ token }: { token: string }) =>
  client.post(`/entity/user-pending`, { tk: token.replaceAll(' ', '+') });

const chosePassword = async (data: { password: string; user: string }) =>
  client.put('/entity/user-pending', data);

const resendVerificationEmail = async (email: string) =>
  client.post('resend-activation-email/', { email });

const getTwoFactorQrCode = async (): Promise<SetUpTwoFactorResponse> =>
  client.post('2fa/setup/?step=1');

const confirmTwoFactorQrCode = async (
  otp: string
): Promise<SetUpTwoFactorStep2Response> =>
  client.post('2fa/setup/?step=2', { otp });

const verifyTwoFactorCode = async ({
  otp,
  days,
}: {
  otp: string;
  days: number | undefined;
}) => client.post('2fa/verify/', { otp, days });

const verifyUserPassword = async ({ password }: { password: string }) =>
  client.post('check-password/', { password });

const disable2Fa = async ({ otp }: { otp: string }) =>
  client.post('2fa/deactivate/', { otp });

const updatePassword = async (
  data: UpdatePasswordRequest,
  idToken: string
): Promise<any> => {
  try {
    const response = await client.put(AUTH_ENDPOINTS.UPDATE_CREDENTIALS, data, {
      headers: {
        Authorization: `${TOKEN_TYPES.BEARER} ${idToken}`,
      },
      isAuthRedirect: false,
    });
    return response;
  } catch (error: any) {
    console.error('Update password request failed:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
    });
    throw error;
  }
};

const getIndsutryCodes = async ({
  search,
}: {
  search: string;
}): Promise<Record<string, string>[]> =>
  client.get(`airwallex/onboarding/industry-code/?search=${search}`);

const getCountries = async ({
  value,
}: {
  value?: string;
}): Promise<CountriesList[]> =>
  // Promise.resolve(mockedCountries);
  client.get(`person/person-country?search=${value ?? ''}`);

const getFederalEntities = async ({
  value,
}: {
  value: string;
}): Promise<FederalEntitiesList[]> =>
  // Promise.resolve(mockedFederal);
  client.get(`enterprise/federal-entity?search=${value ?? ''}`);

const getWhiteLabel = async ({
  organization,
}: IGetWhiteLabel): Promise<IWhiteLabel> =>
  client.get(`public/white-label/${organization}/`);

const enablePaymentLink = async (enterpriseId: string) =>
  client.post(`enterprise/${enterpriseId}/provider/`);

const legacyForgotPassword = async (email: string): Promise<any> => {
  try {
    const response = await client.post(AUTH_ENDPOINTS.LEGACY_FORGOT_PASSWORD, {
      email,
    });
    return response;
  } catch (error: any) {
    console.error('Legacy forgot password request failed:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
    });
    throw error;
  }
};

const legacyResetPassword = async (data: {
  password: string;
  uuid: string;
}): Promise<any> => {
  try {
    const response = await client.post(
      AUTH_ENDPOINTS.LEGACY_RESET_PASSWORD,
      {
        password: aesEncryptCBC(data.password),
        uuid: data.uuid,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        isAuthRedirect: true,
      }
    );
    return response;
  } catch (error: any) {
    console.error('Legacy reset password request failed:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
    });
    throw error;
  }
};

const authSvc = {
  login,
  cognitoLogin,
  cognitoTokenRefresh,
  cognitoLogout,
  cognitoForgotPassword,
  cognitoConfirmPassword,
  userValidate,
  getUserProfile,
  updateProfileInformation,
  registerUser,
  registerHubspotUser,
  validateReferrer,
  getTeam,
  createTeamMember,
  updateTeamMember,
  deleteTeamMember,
  validateInvitationUser,
  chosePassword,
  getBanks,
  tokenRefresh,
  tokenBlacklist,
  validateRegisterEmail,
  resendVerificationEmail,
  getTwoFactorQrCode,
  confirmTwoFactorQrCode,
  verifyTwoFactorCode,
  verifyUserPassword,
  disable2Fa,
  updatePassword,
  getIndsutryCodes,
  getCountries,
  getFederalEntities,
  getWhiteLabel,
  enablePaymentLink,
  getUserPreferences,
  updateUserPreference,
  createUserPreference,
  legacyForgotPassword,
  legacyResetPassword,
};

export default authSvc;
